import { http } from "./http";
import { BASE_URL } from "../data/constants";
import type {
  ArtistHit,
  BlockedUser,
  CheckIn,
  CheckInVisibility,
  FavoriteArtist,
  FollowRequest,
  FollowState,
  OnMyWay,
  Profile,
  SocialUser,
  VenuePresence,
} from "../data/types";

// ------------------------------------------------------------------
// Personsök & profiler
// ------------------------------------------------------------------

export async function searchUsers(q: string): Promise<SocialUser[]> {
  if (q.trim().length < 2) return [];

  const res = await http.get<{ items: SocialUser[] }>(
    `/users/search?q=${encodeURIComponent(q)}`
  );

  return res.items;
}

export function fetchProfile(userId: string) {
  return http.get<Profile>(`/users/${encodeURIComponent(userId)}`);
}

export async function fetchFollowers(userId: string): Promise<SocialUser[]> {
  const res = await http.get<{ items: SocialUser[] }>(
    `/users/${encodeURIComponent(userId)}/followers`
  );

  return res.items;
}

export async function fetchFollowing(userId: string): Promise<SocialUser[]> {
  const res = await http.get<{ items: SocialUser[] }>(
    `/users/${encodeURIComponent(userId)}/following`
  );

  return res.items;
}

// ------------------------------------------------------------------
// Följning
// ------------------------------------------------------------------

export function followUser(userId: string) {
  return http.post<{ followState: FollowState }>(
    `/users/${encodeURIComponent(userId)}/follow`,
    {}
  );
}

export function unfollowUser(userId: string) {
  return http.delete<{ followState: FollowState }>(
    `/users/${encodeURIComponent(userId)}/follow`
  );
}

export async function fetchFollowRequests(): Promise<FollowRequest[]> {
  const res = await http.get<{ items: FollowRequest[] }>("/follows/requests");
  return res.items;
}

export function acceptFollowRequest(requestId: string) {
  return http.post<{ ok: boolean }>(
    `/follows/requests/${encodeURIComponent(requestId)}/accept`,
    {}
  );
}

export function rejectFollowRequest(requestId: string) {
  return http.post<{ ok: boolean }>(
    `/follows/requests/${encodeURIComponent(requestId)}/reject`,
    {}
  );
}

// ------------------------------------------------------------------
// Egen profil
// ------------------------------------------------------------------

export function updateMe(data: {
  firstName?: string;
  lastName?: string;
  bio?: string;
  avatarUrl?: string;
}) {
  return http.patch<unknown>("/me", data);
}

/** Måste matcha MAX_AVATAR_BYTES i api/uploads.js. */
export const MAX_AVATAR_BYTES = 4 * 1024 * 1024;

/** Formaten api/uploads.js accepterar. svg är medvetet uteslutet. */
export const ACCEPTED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Laddar upp en profilbild.
 *
 * Går förbi http.ts eftersom det anropet sätter Content-Type: application/json.
 * En multipart-request måste låta webbläsaren sätta headern själv, annars
 * saknas boundary och multer hittar ingen fil.
 *
 * Endpointen sätter avatarUrl på användaren själv, så det behövs inget
 * separat PATCH /me efteråt.
 */
export async function uploadAvatar(file: File): Promise<string> {
  const form = new FormData();
  form.append("avatar", file);

  const token = localStorage.getItem("auth_token");

  const res = await fetch(`${BASE_URL}/me/avatar`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.error ?? "avatar_upload_failed");
  }

  return data.avatarUrl as string;
}

/** Tar bort profilbilden och återgår till initialerna. */
export async function deleteAvatar(): Promise<void> {
  const token = localStorage.getItem("auth_token");

  const res = await fetch(`${BASE_URL}/me/avatar`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error ?? "avatar_delete_failed");
  }
}

/** Favoriter sparas med Beatspots interna artist-ID, inte med källans. */
export function saveFavoriteArtists(artistIds: string[]) {
  return http.put<{ items: FavoriteArtist[] }>("/api/me/favorite-artists", {
    artistIds,
  });
}

/** Startförslag till onboarding, rankade på källans popularitetssiffra. */
export async function fetchPopularArtists(limit = 24): Promise<ArtistHit[]> {
  const res = await fetch(`${BASE_URL}/api/catalog/artists/popular?limit=${limit}`, {
    headers: { Accept: "application/json" },
  });

  if (!res.ok) return [];

  const json: { items: ArtistHit[] } = await res.json();
  return json.items ?? [];
}

export function fetchFavoriteArtists() {
  return http.get<{ items: FavoriteArtist[] }>("/api/me/favorite-artists");
}

/**
 * Artistsök går mot Beatspots egen katalog. Den fylls manuellt av
 * scripts/importDeezerCatalog.js — klienten pratar aldrig med någon extern
 * musiktjänst.
 */
export async function searchArtists(q: string): Promise<ArtistHit[]> {
  if (q.trim().length < 2) return [];

  const res = await fetch(
    `${BASE_URL}/api/catalog/artists?q=${encodeURIComponent(q)}&limit=20`,
    { headers: { Accept: "application/json" } }
  );

  if (!res.ok) return [];

  const json: { items: ArtistHit[] } = await res.json();
  return json.items ?? [];
}

// ------------------------------------------------------------------
// Check-ins
// ------------------------------------------------------------------

export function fetchActiveCheckIn() {
  return http.get<{ checkIn: CheckIn | null }>("/checkins/active");
}

/**
 * visibility och recipientIds skickas bara när användaren valt en krets.
 * Servern validerar mottagarna mot godkända följningar och avvisar resten
 * — klienten filtrerar för bekvämlighet, inte för säkerhet.
 */
export function checkIn(data: {
  venueId: string;
  note?: string;
  lat?: number;
  lon?: number;
  visibility?: CheckInVisibility;
  recipientIds?: string[];
  expiresInMinutes?: number;
}) {
  return http.post<{ checkIn: CheckIn; deduped?: boolean }>("/checkins", data);
}

export function endCheckIn() {
  return http.post<{ ok: boolean }>("/checkins/end", {});
}

export function fetchVenueCheckIns(venueId: string) {
  return http.get<{ defaultMinutes: number; maxMinutes: number; items: CheckIn[] }>(
    `/venues/${encodeURIComponent(venueId)}/checkins`
  );
}

/** Vilka som är där och vilka som är på väg, i ett svar. */
export function fetchVenuePresence(venueId: string) {
  return http.get<VenuePresence>(
    `/venues/${encodeURIComponent(venueId)}/presence`
  );
}

// ------------------------------------------------------------------
// På väg
// ------------------------------------------------------------------

export function fetchActiveOnMyWay() {
  return http.get<{ onMyWay: OnMyWay | null }>("/on-my-way/active");
}

/**
 * Utgår man från någons check-in räcker checkInId — servern hämtar stället
 * därifrån, så klienten kan inte råka peka resan mot fel venue.
 */
export function startOnMyWay(data: {
  venueId?: string;
  checkInId?: string;
  etaMinutes: number;
}) {
  return http.post<{ onMyWay: OnMyWay }>("/on-my-way", data);
}

export function updateOnMyWayEta(id: string, etaMinutes: number) {
  return http.patch<{ onMyWay: OnMyWay }>(
    `/on-my-way/${encodeURIComponent(id)}`,
    { etaMinutes }
  );
}

export function cancelOnMyWay(id: string) {
  return http.post<{ onMyWay: OnMyWay }>(
    `/on-my-way/${encodeURIComponent(id)}/cancel`,
    {}
  );
}

/**
 * Ankomsten. Position skickas när vi har den; servern avgör om den krävs.
 * Svaret bär check-in:en backend skapade — klienten hittar inte på någon
 * egen, och alreadyArrived säger att en tidigare request redan hann först.
 */
export function arriveOnMyWay(
  id: string,
  data: { lat?: number; lon?: number } = {}
) {
  return http.post<{
    onMyWay: OnMyWay;
    checkIn: CheckIn | null;
    alreadyArrived: boolean;
  }>(`/on-my-way/${encodeURIComponent(id)}/arrive`, data);
}

// ------------------------------------------------------------------
// Blockering och anmälan
// ------------------------------------------------------------------

export function blockUser(userId: string) {
  return http.post<{ ok: boolean; blocked: boolean }>(
    `/users/${encodeURIComponent(userId)}/block`,
    {}
  );
}

export function unblockUser(userId: string) {
  return http.delete<{ ok: boolean; blocked: boolean }>(
    `/users/${encodeURIComponent(userId)}/block`
  );
}

export async function fetchBlocks(): Promise<BlockedUser[]> {
  const res = await http.get<{ items: BlockedUser[] }>("/me/blocks");
  return res.items ?? [];
}

export function reportUser(data: {
  userId?: string;
  checkInId?: string;
  reason: string;
  details?: string;
}) {
  return http.post<{ report: { id: string; createdAt: string } }>("/reports", data);
}
